/**************************************************************************************************/
/*   File: /src/app/stores/reducers/ui/reducer.ts                                                 */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Friday February 22nd 2019                                                      */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Tue May 14 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import * as _ from 'lodash';

import { environment } from '../../../../environments/environment';
import * as fromEnums from '../../../shared/enums';
import * as fromModels from '../../../shared/models';
import * as fromActions from '../../actions';
import * as fromStates from '../../states';

/**
 * The ui state feature reducer
 * @param state The state
 * @param action The action
 */
export function uiReducer(
  state: fromStates.fromUi.State = fromStates.fromUi.INITIAL_UI_STATE,
  action: fromActions.fromUi.Actions | fromActions.fromMessages.MessagesActions): fromStates.fromUi.State {

  switch (action.type) {
    // Reducer for language change
    case fromActions.fromUi.UiActionTypes.LANGUAGE_CHANGE_ACTION:
      return {
        ...state,
        isLoadingLanguageChange: true
      };
    // Reducer for language change success
    case fromActions.fromUi.UiActionTypes.LANGUAGE_CHANGE_SUCCESS_ACTION:
      const newLanguages = [...state.languages];
      newLanguages.forEach(x => x.selected = false);
      newLanguages.find(x => x.isoCode === action.payload.isoCode).selected = true;

      return {
        ...state,
        languages: [
          ...newLanguages
        ],
        // messages: [],
        isLoadingLanguageChange: false
      };
    // Reducer for change language failure
    case fromActions.fromUi.UiActionTypes.LANGUAGE_CHANGE_FAILURE_ACTION:
      const newLanguagesFail = [...state.languages];
      newLanguagesFail.forEach(x => x.selected = false);
      newLanguagesFail.find(x => x.isoCode === environment.defaultLanguage).selected = true;
      return {
        ...state,
        isLoadingLanguageChange: false,
        languages: [
          ...newLanguagesFail
        ],
        messages: [
          action.payload.message
        ]
      };
    // Reducer for set user
    case fromActions.fromUi.UiActionTypes.SET_LOGGED_USER:
      return {
        ...state,
        user: action.payload.user
      };

    // Reducer for unset user
    case fromActions.fromUi.UiActionTypes.UNSET_LOGGED_USER:
      return {
        ...state,
        user: undefined
      };
    // Reducer for load dossier status
    case fromActions.fromUi.UiActionTypes.LOAD_DOSSIER_STATUS:
      return {
        ...state,
        isLoadingDossierStatus: true,
        dossierStatus: []
      };
    // Reducer for load dossier status success
    case fromActions.fromUi.UiActionTypes.LOAD_DOSSIER_STATUS_SUCCESS:
      return {
        ...state,
        isLoadingDossierStatus: false,
        dossierStatus: action.payload.status
      };
    // Reducer for load the plant types
    case fromActions.fromUi.UiActionTypes.LOAD_PLAN_TYPES:
      return {
        ...state,
        isLoadingPlanTypes: true,
        planTypes: []
      };
    // Reducer for load plant types success
    case fromActions.fromUi.UiActionTypes.LOAD_PLAN_TYPES_SUCCESS:
      return {
        ...state,
        isLoadingPlanTypes: false,
        planTypes: action.payload.planTypes
      };
    // Reducer for load plant types fail
    case fromActions.fromUi.UiActionTypes.LOAD_PLAN_TYPES_FAIL:
      return {
        ...state,
        isLoadingPlanTypes: false,
        planTypes: [],
        messages: [
          action.payload.message
        ]
      };
    // Reducer for load the ambiti
    case fromActions.fromUi.UiActionTypes.LOAD_ALL_AMBITI:
      return {
        ...state,
        isLoadingAmbiti: true,
        ambiti: [],
        messages: []
      };
    // Reducer for ambiti success
    case fromActions.fromUi.UiActionTypes.LOAD_ALL_AMBITI_SUCCESS:
      if (!action.payload.ambiti || action.payload.ambiti.length === 0) {
        return {
          ...state,
          isLoadingAmbiti: false,
          ambiti: [],
          messages: [
            new fromModels.Message(
              fromEnums.MessageType.ERROR,
              fromEnums.Messages.NO_AMBITI_FOR_PLAN,
              fromEnums.MessagePositionEnum.TOP,
              fromStates.fromUi.STATE_NAME
            )
          ]
        };
      }
      return {
        ...state,
        isLoadingAmbiti: false,
        ambiti: action.payload.ambiti
      };
    // Reducer for ambiti fail
    case fromActions.fromUi.UiActionTypes.LOAD_ALL_AMBITI_FAIL:
      return {
        ...state,
        isLoadingAmbiti: false,
        ambiti: [],
        messages: [
          action.payload.message
        ]
      };
    // Reducer for load the ambiti
    case fromActions.fromUi.UiActionTypes.LOAD_AMBITI_BY_PLAN:
      return {
        ...state,
        isLoadingAmbiti: true,
        ambiti: [],
        messages: []
      };
    // Reducer for ambiti success
    case fromActions.fromUi.UiActionTypes.LOAD_AMBITI_BY_PLAN_SUCCESS:
      if (!action.payload.ambiti || action.payload.ambiti.length === 0) {
        return {
          ...state,
          isLoadingAmbiti: false,
          ambiti: [],
          messages: [
            new fromModels.Message(
              fromEnums.MessageType.ERROR,
              fromEnums.Messages.NO_AMBITI_FOR_PLAN,
              fromEnums.MessagePositionEnum.TOP,
              fromStates.fromUi.STATE_NAME
            )
          ]
        };
      }
      return {
        ...state,
        isLoadingAmbiti: false,
        ambiti: action.payload.ambiti
      };
    // Reducer for ambiti fail
    case fromActions.fromUi.UiActionTypes.LOAD_AMBITI_BY_PLAN_FAIL:
      return {
        ...state,
        isLoadingAmbiti: false,
        ambiti: [],
        messages: [
          action.payload.message
        ]
      };
    // Reducer for geo file types
    case fromActions.fromUi.UiActionTypes.LOAD_GEO_DATA_TYPES:
      return {
        ...state,
        isLoadingGeoDataTypes: true,
        geoFileTypes: []
      };
    // Reducer for geo file types
    case fromActions.fromUi.UiActionTypes.LOAD_GEO_DATA_TYPES_SUCCESS:
      return {
        ...state,
        isLoadingGeoDataTypes: false,
        geoFileTypes: action.payload.fileTypes
      };
    // Reducer for message removed
    case fromActions.fromMessages.MessagesActionsType.REMOVE_MESSAGE_ACTION:
      return {
        ...state,
        messages: [...state.messages.filter(x => x !== action.payload.message)]
      };
    // Reducer for notify message
    case fromActions.fromMessages.MessagesActionsType.NOTIFY_MESSAGE:
      return action.payload.message.stateName === fromStates.fromUi.STATE_NAME ?
        {
          ...state,
          messages: [
            ...state.messages,
            action.payload.message
          ]
        } : { ...state };
    default:
      return state;
  }
}

// Helpers
/**
 * The get isLoading helper function
 * @param state The state
 */
const getIsLoading = (state: fromStates.fromUi.State) => 
  state.isLoadingLanguageChange ||
  state.isLoadingDossierStatus ||
  state.isLoadingPlanTypes ||
  state.isLoadingAmbiti ||
  state.isLoadingGeoDataTypes;
/**
 * The get available languages helper
 * @param state The state
 */
const getLanguages = (state: fromStates.fromUi.State) => state.languages;
/**
 * The get messages helper function
 * @param state The state
 */
const getMessages = (state: fromStates.fromUi.State) => state.messages;
/**
 * The get logged user helper function
 * @param state The state
 */
const getLoggedUser = (state: fromStates.fromUi.State) => state.user;
/**
 * Get dossier status
 * @param state The state
 */
const getDossierStatus = (state: fromStates.fromUi.State) => state.dossierStatus;
/**
 * Get the plant types
 * @param state The state
 */
const getPlanTypes = (state: fromStates.fromUi.State) => state.planTypes;
/**
 * Get the ambiti
 * @param state The state
 */
const getAmbiti = (state: fromStates.fromUi.State) => state.ambiti;
/**
 * The getTypes helper function
 * @param state The state
 */
const getGeoFileTypes = (state: fromStates.fromUi.State) => state.geoFileTypes;


/**
 * Export the helpers
 */
export const helpers = {
  getIsLoading,
  getLanguages,
  getMessages,
  getLoggedUser,
  getDossierStatus,
  getPlanTypes,
  getAmbiti,
  getGeoFileTypes
};
